import { z } from 'zod';
import { amountSchema, currencyCodeSchema, dateOnlySchema } from './common';
import { patchOf } from './zod-setup';
import { advancedFilterQueryFragment } from './record-filters';
/**
 * Bank and cash accounts, and the transactions on them.
 *
 * This is the Zoho Books banking module minus live bank feeds: accounts,
 * manual entry, statement import, transfers, and match-or-categorise. No
 * aggregator, and no double-entry - a transaction points at the document it
 * resolves to, and the balance is the sum of transactions.
 */
/** Money you can spend: what "cash", "runway" and "in the bank" add up. */
export const CASH_ACCOUNT_TYPES = ['bank', 'credit_card', 'cash', 'other'];
/**
 * Money put away: a fixed or recurring deposit, a mutual fund or SIP, a chit.
 *
 * ── WHY THEY ARE ACCOUNTS AND NOT A CATEGORY ──
 * A ₹10,000 FD made from the current account is not spent. Filing it as an
 * expense (the only door there was) put it in the P&L and lost the asset;
 * leaving it unfiled left the bank balance ₹10,000 above the statement.
 * As an account, the money moves by TRANSFER - out of the bank, into the FD -
 * which no income or expense figure reads, and the FD's balance is derived
 * the same way every other balance is.
 *
 * ── AND WHY THEY ARE KEPT OUT OF CASH ──
 * An FD is not runway: it cannot pay this month's rent without breaking it.
 * Every figure that says "cash" reads `isInvestmentAccountType` and leaves
 * these out; they are reported beside it as investments, and added in only
 * where the question is net worth.
 */
export const INVESTMENT_ACCOUNT_TYPES = [
    'fixed_deposit',
    'recurring_deposit',
    'mutual_fund',
    'chit',
    'other_investment',
];
export const BANK_ACCOUNT_TYPES = [...CASH_ACCOUNT_TYPES, ...INVESTMENT_ACCOUNT_TYPES];
const INVESTMENT_TYPE_SET = new Set(INVESTMENT_ACCOUNT_TYPES);
/** True for an FD, RD, fund, chit or other investment - never counted as cash. */
export function isInvestmentAccountType(type) {
    return !!type && INVESTMENT_TYPE_SET.has(type);
}
/** A percentage a year, as typed: "7.1", "7.10 %". Kept as a string, like money. */
const annualRateSchema = z
    .union([z.string().trim(), z.number()])
    .transform((v) => (typeof v === 'number' ? String(v) : v.replace(/\s*%$/, '')))
    .refine((v) => /^\d{1,3}(\.\d{1,3})?$/.test(v) && Number(v) <= 100, {
    message: 'Enter the rate as a percentage a year, e.g. 7.1',
});
export const createBankAccountSchema = z
    .object({
    name: z.string().trim().min(1, 'Name the account').max(120),
    type: z.enum(BANK_ACCOUNT_TYPES).default('bank'),
    bankName: z.string().trim().max(120).optional(),
    /**
     * Last four digits only, and that is deliberate.
     *
     * A full account number buys nothing here - Fint never initiates a payment,
     * so it is data we can only lose, and the last four is what a human needs
     * to tell two accounts apart in a picker. The UI accepts a full number and
     * keeps the tail.
     */
    accountNumberLast4: z.string().trim().regex(/^\d{0,4}$/, 'Just the last four digits').optional(),
    ifsc: z.string().trim().toUpperCase().max(20).optional(),
    currency: currencyCodeSchema.optional(),
    /**
     * The balance on the day the account started being tracked in Fint,
     * with the date it applies to. Without the date, an imported statement
     * that predates it would be added to a balance it is already inside.
     */
    openingBalance: amountSchema.optional(),
    openingBalanceDate: dateOnlySchema.optional(),
    description: z.string().trim().max(500).optional(),
    /** The account defaulted onto new payments and expenses. */
    isPrimary: z.boolean().default(false),
    isActive: z.boolean().default(true),
    // ── An investment's terms. Only for INVESTMENT_ACCOUNT_TYPES; the service
    // refuses them on a bank, card or cash account. None is a figure: the
    // balance is still opening + transactions, so these describe the deposit
    // (for the register and the maturity calendar) and never move money.
    // `bankName` is the institution, `accountNumberLast4` the tail of the
    // FD / folio / chit number.
    /** What was put in at the start - for an FD, the deposit itself. */
    principalAmount: amountSchema.nullable().optional(),
    /** Percent a year. Null for a fund, whose return is not a rate. */
    interestRate: annualRateSchema.nullable().optional(),
    startDate: dateOnlySchema.nullable().optional(),
    maturityDate: dateOnlySchema.nullable().optional(),
    /** Renews on maturity rather than paying out. */
    autoRenew: z.boolean().optional(),
})
    .strict();
/** The fields above that only an investment account may carry. */
export const INVESTMENT_TERM_FIELDS = ['principalAmount', 'interestRate', 'startDate', 'maturityDate', 'autoRenew'];
// patchOf, not .partial(): under zod 4 a rename would otherwise reset type,
// isPrimary and isActive to their create-time defaults. See zod-setup.ts.
export const updateBankAccountSchema = patchOf(createBankAccountSchema).strict();
// ── Transactions ──────────────────────────────────────────────────────────
/**
 * Money in or out. Signed `amount` would be ambiguous on a credit card, where
 * a "deposit" is a repayment, so direction is its own field.
 */
export const TRANSACTION_DIRECTIONS = ['deposit', 'withdrawal'];
/**
 * Where a transaction is in the reconciliation flow.
 *
 * `uncategorised` came in from a statement and means nothing yet
 * `categorised`  a human said what it was; Fint created the expense/payment
 * `matched`      linked to a document that already existed
 * `excluded`     deliberately ignored - a duplicate, or a transfer already
 *                recorded from the other side
 *
 * `excluded` is not `deleted`: the row stays so a re-import recognises it and
 * does not offer it again.
 *
 * `draft` is deliberately NOT in this list. A draft is a proposal a model made
 * (migration 0015), in no balance until a person confirms it, and the list's
 * `status=all` means "everything recorded" - a proposal is shown only when
 * asked for by name, so no existing screen starts showing money nobody has
 * agreed to.
 */
export const TRANSACTION_STATUSES = ['uncategorised', 'categorised', 'matched', 'excluded'];
/** What a categorised or matched transaction resolves to. */
export const TRANSACTION_LINK_TYPES = [
    'expense',
    'payment_received',
    'vendor_payment',
    'transfer',
    'owner_drawing',
    'owner_contribution',
    'other_income',
    'other_expense',
];
export const createBankTransactionSchema = z
    .object({
    accountId: z.string().trim().min(1).max(36),
    date: dateOnlySchema,
    direction: z.enum(TRANSACTION_DIRECTIONS),
    amount: amountSchema,
    description: z.string().trim().max(300).optional(),
    referenceNumber: z.string().trim().max(120).optional(),
    payee: z.string().trim().max(200).optional(),
    notes: z.string().trim().max(2000).optional(),
})
    .strict();
/** Say what an uncategorised transaction was, and create the record for it. */
export const categoriseTransactionSchema = z
    .object({
    linkType: z.enum(TRANSACTION_LINK_TYPES),
    /** For an expense: which category. For income: which account. */
    categoryId: z.string().trim().max(36).nullable().optional(),
    customerId: z.string().trim().max(36).nullable().optional(),
    vendorId: z.string().trim().max(36).nullable().optional(),
    /** For a transfer: the other account. */
    counterpartAccountId: z.string().trim().max(36).nullable().optional(),
    taxId: z.string().trim().max(36).nullable().optional(),
    notes: z.string().trim().max(2000).optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.linkType === 'transfer' && !v.counterpartAccountId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['counterpartAccountId'],
            message: 'A transfer needs the account on the other side.',
        });
    }
    if (v.linkType === 'expense' && !v.categoryId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['categoryId'], message: 'Pick an expense category.' });
    }
});
/** Link a transaction to a document that already exists. */
export const matchTransactionSchema = z
    .object({
    linkType: z.enum(TRANSACTION_LINK_TYPES),
    linkId: z.string().trim().min(1).max(36),
})
    .strict();
export const createTransferSchema = z
    .object({
    fromAccountId: z.string().trim().min(1).max(36),
    toAccountId: z.string().trim().min(1).max(36),
    date: dateOnlySchema,
    amount: amountSchema,
    referenceNumber: z.string().trim().max(120).optional(),
    notes: z.string().trim().max(2000).optional(),
})
    .strict()
    .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: 'Pick two different accounts.',
    path: ['toAccountId'],
});
// ── Statement import ──────────────────────────────────────────────────────
/**
 * How the columns of an uploaded file map onto a transaction.
 *
 * Every bank exports a different CSV, so the mapping is per-import and
 * remembered per account for next time. Values are column indexes.
 */
export const statementMappingSchema = z
    .object({
    date: z.number().int().min(0),
    description: z.number().int().min(0).optional(),
    referenceNumber: z.number().int().min(0).optional(),
    payee: z.number().int().min(0).optional(),
    /**
     * Two shapes in the wild: one signed amount column, or separate debit and
     * credit columns. Both are supported because forcing the user to reshape
     * their bank's export in a spreadsheet first defeats the point.
     */
    amount: z.number().int().min(0).optional(),
    debit: z.number().int().min(0).optional(),
    credit: z.number().int().min(0).optional(),
    /** How dates are written in this file, e.g. `DD/MM/YYYY`. */
    dateFormat: z.string().trim().min(1).max(20).default('DD/MM/YYYY'),
    /** Rows to skip before the data starts. */
    headerRows: z.number().int().min(0).max(50).default(1),
    /** Some banks export withdrawals as positive numbers in a single column. */
    invertAmount: z.boolean().default(false),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.amount === undefined && v.debit === undefined && v.credit === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['amount'],
            message: 'Map either a single amount column, or a debit and credit pair.',
        });
    }
});
export const importStatementSchema = z
    .object({
    accountId: z.string().trim().min(1).max(36),
    fileId: z.string().trim().min(1).max(36),
    mapping: statementMappingSchema,
    /** Preview the parse without writing anything. */
    dryRun: z.boolean().default(false),
})
    .strict();
export const listTransactionsQuerySchema = z
    .object({
    accountId: z.string().trim().max(36).optional(),
    status: z.enum(['all', ...TRANSACTION_STATUSES, 'draft']).default('all'),
    direction: z.enum(['all', ...TRANSACTION_DIRECTIONS]).default('all'),
    q: z.string().trim().max(200).optional(),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    ...advancedFilterQueryFragment,
})
    .strict();
/**
 * Correct a bank line a person or a statement put there - its date, its
 * amount, its description or the account it is on.
 *
 * Sai: "editable everything by an admin ... with audit trail". A line Fint
 * WROTE - the cash side of a payment, an expense, a refund, one leg of a
 * transfer - is not editable here: it follows its record, and correcting it
 * means correcting that record. A draft has its own edit in the Inbox.
 *
 * `reason` is required for the reason a payment correction requires one:
 * the line is what a bank statement said, and the trail has to say why Fint
 * now says something else, in the person's words, beside what changed.
 */
export const updateBankTransactionSchema = z
    .object({
    date: dateOnlySchema.optional(),
    amount: amountSchema.optional(),
    description: z.string().trim().max(300).optional(),
    accountId: z.string().trim().min(1).max(36).optional(),
    reason: z.string().trim().min(3, 'Say why this line is being corrected').max(500),
})
    .strict()
    .refine((v) => v.date !== undefined || v.amount !== undefined || v.description !== undefined || v.accountId !== undefined, {
    message: 'Change the date, the amount, the description or the account',
});
//# sourceMappingURL=banking.js.map